import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, UntypedFormArray, UntypedFormControl } from '@angular/forms';
import { PfiService } from './mock/pfi.service';
import {
  FreightMethod,
  GenFreightMethodService,
  GenIncotermService,
  GenWarehouseService,
  Incoterm,
  Proforma,
  VsExpectedIwd,
  VsAdditionalCharge,
  Warehouse
} from 'coadapi';
import { combineLatest, Observable, of, Subscription } from 'rxjs';
import { shareReplay, switchMap, take } from 'rxjs/operators';
import { MatDialog as MatDialog } from '@angular/material/dialog';
import { InlandChargesDialogComponent } from './components/inland-charges-dialog/inland-charges-dialog.component';

interface PredictionData {
  pricePerKilo: number;
  totGoods: number;
  totFreight: number;
  predPercentage: number;
  predAccuracy: number;
}

interface GroupedPrediction {
  methodID: number;
  method: string;
  incoterm: string;
  warehouses: { [warehouseID: number]: PredictionData };
}

interface ServerValidationError {
  field: string;
  message: string;
  controlPath?: string;
}

const requiredDateFields = ['paymentReceivedBy', 'dispatchBy', 'deliveryDate'];

@Component({
  selector: 'app-proforma-value',
  templateUrl: './proforma-value.component.html',
  styleUrls: ['./proforma-value.component.scss']
})
export class ProformaValueComponent implements OnInit {
  proformaForm!: UntypedFormGroup;
  serverValidationErrors: ServerValidationError[] = [];
  saveErrorMessage = '';
  saveStatus: 'idle' | 'saving' | 'success' | 'error' = 'idle';
  saveStatusMessage = '';
  iwdClearedNotice = '';
  readonly costWarningThresholdPercent = 30;
  private serverErrorSubscriptions: Subscription[] = [];
  displayedColumnsSummary: string[] = ['warehouse', 'warehouseLineCount', 'goodsValue', 'grossProfit', 'handling', 'inland', 'vsexport', 'total'];
  displayedColumnsPredictions: string[] = [];

  vsIwdFields = [
    {label: 'From', controlName: 'from', type: 'text'},
    {label: 'To', controlName: 'to', type: 'text'},
    {label: 'Cost', controlName: 'cost', type: 'number'}
  ];

  vsBreakdownFields = [
    {label: 'Freight (To Port Only)', controlName: 'freightToPort', type: 'number', supportsPercentage: true},
    {label: 'Destination Charge', controlName: 'destinationCharge', type: 'number'},
    {label: 'Duties & Taxes', controlName: 'dutiesAndTaxes', type: 'number', supportsPercentage: true},
    {label: 'Legalisation / CI', controlName: 'legalisation', type: 'number'},
    {label: 'Cert Of Origin', controlName: 'certOfOrigin', type: 'number'}
  ];

  vsAdditionalDetailsFields = [
    {label: 'Leaving From', controlName: 'leavingFrom', type: 'text'},
    {label: 'Method/Incoterm', controlName: 'methodIncoterm', type: 'text'},
    {label: 'Incoterm Location', controlName: 'incotermLocation', type: 'text'},
    {label: 'Destination Port', controlName: 'destinationPort', type: 'text'},
    {label: 'Payment Received By', controlName: 'paymentReceivedBy', type: 'date'},
    {label: 'Dispatch By', controlName: 'dispatchBy', type: 'date'},
    {label: 'Delivery Date', controlName: 'deliveryDate', type: 'date'}
  ];

  uniqueWarehouses: { id: number; name: string; colDef: string }[] = [];
  displayedColumnsBreakdown: string[] = ['chargeName', 'chargeValue'];
  pfi: any = null;
  initialProformaId: number | string | null = null;

  private warehouses$!: Observable<Warehouse[]>;
  private methods$!: Observable<FreightMethod[]>;
  private incoterms$!: Observable<Incoterm[]>;

  warehouseList!: Warehouse[];
  methodList!: FreightMethod[];
  incotermList!: Incoterm[];

  get iwdWarehouseList(): Warehouse[] {
    const deliveryToId = this.proformaForm?.get('vsAdditionalDetails.leavingFrom')?.value?.warehouseId;
    return (this.warehouseList ?? []).filter(warehouse => warehouse.warehouseId !== deliveryToId);
  }

  get relevantIncotermList(): Incoterm[] {
    const methodId = this.proformaForm?.get('vsAdditionalDetails.freightMethod')?.value?.methodId;
    return methodId == null
      ? []
      : (this.incotermList ?? []).filter(incoterm =>
        incoterm.relevantTo?.some(method => method.methodId === methodId)
      );
  }

  constructor(
    private fb: UntypedFormBuilder,
    private pfiService: PfiService,
    private warehouseService: GenWarehouseService,
    private freightMethodService: GenFreightMethodService,
    private incotermService: GenIncotermService,
    private dialog: MatDialog,
    private route: ActivatedRoute,
    private router: Router,
  ) {
  }

  ngOnInit(): void {
    const routeProformaId = this.route.snapshot.queryParamMap.get('proformaId');
    if (routeProformaId) {
      this.initialProformaId = Number.isFinite(Number(routeProformaId)) ? Number(routeProformaId) : routeProformaId;
      this.setNewPfi({proformaId: this.initialProformaId}, false);
    }

    this.methods$ = this.freightMethodService.getFreightMethods().pipe(shareReplay(1));
    this.incoterms$ = this.incotermService.getAllIncoterms().pipe(shareReplay(1));
    this.warehouses$ = this.warehouseService.getWarehouses().pipe(shareReplay(1));

    combineLatest([this.warehouses$, this.methods$, this.incoterms$])
      .pipe(take(1))
      .subscribe(([warehouses, methods, incoterms]) => {
        this.warehouseList = warehouses;
        this.methodList = methods;
        this.incotermList = incoterms;
        const selectedMethod = this.proformaForm?.get('vsAdditionalDetails.freightMethod')?.value;
        if (selectedMethod) {
          this.onFreightMethodChange(selectedMethod);
        }
      });
  }

  get vsSplit(): UntypedFormArray {
    return this.proformaForm.get('vsSplit') as UntypedFormArray;
  }

    get vsSplitRows(): UntypedFormGroup[] {
      return this.vsSplit?.controls as UntypedFormGroup[] ?? [];
    }

  get vsPredictions(): UntypedFormArray {
    return this.proformaForm.get('vsPredictions') as UntypedFormArray;
  }

  get vsPredictionRows(): UntypedFormGroup[] {
    return this.vsPredictions?.controls as UntypedFormGroup[] ?? [];
  }

  get vsBreakdown(): UntypedFormArray {
    return this.proformaForm.get('vsBreakdown') as UntypedFormArray;
  }

  get freightToBeConfirmedControl(): UntypedFormControl {
    return this.proformaForm.get('vsAdditionalDetails.freightToBeConfirmed') as UntypedFormControl;
  }

  setNewPfi(pfi: any, updateUrl = true): void {
    if (updateUrl) {
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: {proformaId: pfi.proformaId},
        queryParamsHandling: 'merge',
        replaceUrl: true
      });
    }

    this.pfiService.getProformaById(pfi.proformaId).subscribe(fullPfiData => {
      if (fullPfiData) {
        this.init(fullPfiData);
      } else {
        // handle error
      }
    });
  }

  get currencySymbol(): string {
    return this.pfi && this.pfi.pfiCurrency && this.pfi.pfiCurrency.currencyCode === 'GBP'
      ? '£'
      : '$';
  }

  private init(pfi: any): void {
    this.pfi = this.normalisePfi(pfi);
    this.proformaForm = this.fb.group({
      pfiId: this.pfi.pfiId,
      companyName: [null],
      accountNo: [null],
      invoicedBy: [null],
      showDisclaimerOnCi: [false],
      releaseTypeName: [null],
      descriptiveType: [null],
      imagesRequired: [false],
      splitShipments: [false],
      inspectionRequired: [false],
      abbreviation: [null],
      agentCompanyName: [null],
      addressDetail: [null],
      fullName: [null],
      telephone: [null],
      workEmail: [null],
      agentFullName: [null],
      agentTelephone: [null],
      agentWorkEmail: [null],
      preferredContactFullName: [null],
      preferredAgentFullName: [null],
      notAvailable: [null]
    });
    this.initPfiForm();
  }

  private normalisePfi(response: any): any {
    const staticData = response?.static ?? response ?? {};
    const dynamicData = response?.dynamic ?? {};

    return {
      ...staticData,
      ...dynamicData,
      pfiId: response?.pfiId ?? staticData.pfiId ?? dynamicData.pfiId
    };
  }

  private initPfiForm(): void {
    const {
      pfiId,
      modelClient,
      vsSplit,
      notAvailable,
      vsIwd,
      vsPredictions,
      vsBreakdown,
      vsAdditionalDetails,
      pfiDeliveryAddress
    } = this.pfi;

    // Use default empty objects if properties are missing.
    const {
      companyName,
      accountNo,
      invoicedBy,
      releaseType,
      showDisclaimerOnCi,
      descriptive,
      imagesRequired,
      splitShipments,
      inspectionRequired,
      preferredAgent,
      preferredAgentContact
    } = modelClient || {};

    const {name: releaseTypeName} = releaseType || {};
    const preferredAgentFullName = preferredAgent ? preferredAgent.companyName : '';
    const preferredContactFullName = preferredAgentContact ? preferredAgentContact.fullName : '';
    const {type} = descriptive || {};
    const {addressDetail} = pfiDeliveryAddress || {};
    const incotermAbbreviation = vsAdditionalDetails?.incoterm?.abbreviation ?? null;

    this.proformaForm.addControl('vsSplit', this.initSplit(vsSplit));
    this.proformaForm.addControl('vsIwd', this.initIwd(vsIwd));
    this.proformaForm.addControl('vsPredictions', this.initPredictions(vsPredictions));
    this.proformaForm.addControl('vsBreakdown', this.initBreakdown(vsBreakdown));
    this.proformaForm.addControl('vsAdditionalDetails', this.initAdditionalDetails(vsAdditionalDetails));
    this.proformaForm.get('vsAdditionalDetails.leavingFrom')?.valueChanges.subscribe(() => this.onIwdDestinationChange());
    this.updateIncotermLocationValidation(
      this.proformaForm.get('vsAdditionalDetails.freightMethod')?.value
    );

    this.proformaForm.controls.pfiId.setValue(pfiId || null);
    this.proformaForm.controls.companyName.setValue(companyName || null);
    this.proformaForm.controls.accountNo.setValue(accountNo || null);
    this.proformaForm.controls.invoicedBy.setValue(invoicedBy || null);
    this.proformaForm.controls.releaseTypeName.setValue(releaseTypeName || null);
    this.proformaForm.controls.showDisclaimerOnCi.setValue(showDisclaimerOnCi);
    this.proformaForm.controls.descriptiveType.setValue(type || null);
    this.proformaForm.controls.imagesRequired.setValue(imagesRequired);
    this.proformaForm.controls.splitShipments.setValue(splitShipments);
    this.proformaForm.controls.inspectionRequired.setValue(inspectionRequired);
    this.proformaForm.controls.abbreviation.setValue(incotermAbbreviation);
    this.proformaForm.controls.addressDetail.setValue(addressDetail || null);
    this.proformaForm.controls.preferredAgentFullName.setValue(preferredAgentFullName || null);
    this.proformaForm.controls.preferredContactFullName.setValue(preferredContactFullName || null);
    this.proformaForm.controls.agentTelephone.setValue(preferredAgentContact?.telephone ?? null);
    this.proformaForm.controls.agentWorkEmail.setValue(preferredAgentContact?.workEmail ?? null);
    this.proformaForm.controls.notAvailable.setValue(notAvailable ?? 0);
    this.onIwdDestinationChange();
    this.onToggleFreightToBeConfirmed();
  }

  onSubmit(): void {
    this.saveStatus = 'idle';
    const incotermLocationControl = this.proformaForm.get('vsAdditionalDetails.incotermLocation');
    if (incotermLocationControl?.invalid) {
      incotermLocationControl.markAsTouched();
      this.saveStatus = 'error';
      this.saveStatusMessage = 'Not saved - please enter an Incoterm Location.';
      this.scrollToTop();
      return;
    }

    if (!this.areDatesReadyToSave()) {
      this.saveStatus = 'error';
      this.saveStatusMessage = 'Not saved - please correct the highlighted dates.';
      this.scrollToTop();
      return;
    }

    this.saveStatus = 'saving';
    this.saveStatusMessage = 'Saving...';
    this.clearServerValidationErrors();
    this.serverValidationErrors = [];
    this.saveErrorMessage = '';

    const {vsIwd, ...convertedData} = this.convertToPfiEntity(this.proformaForm.getRawValue());
    const pfiData = {...this.pfi, ...convertedData};
    delete pfiData.vsIwd;

    this.pfiService.updatePfi(pfiData)
      .pipe(switchMap(() => this.saveIwdCharge(vsIwd)))
      .subscribe(savedIwd => {
        if (savedIwd?.expectedIwdId != null) {
          this.proformaForm.get('vsIwd.expectedIwdId')?.setValue(savedIwd.expectedIwdId);
        }
        this.saveStatus = 'success';
        this.saveStatusMessage = 'Value sheet saved successfully.';
      }, error => this.handleSaveError(error));
  }

  private handleSaveError(error: any): void {
    this.saveStatus = 'error';
    this.saveStatusMessage = 'Save failed - see the errors at the top of the page.';
    this.scrollToTop();
    const response = error?.error ?? {};
    const validationDictionary = response.errors ?? response.Errors ?? response.ModelState;

    if (validationDictionary && typeof validationDictionary === 'object' && !Array.isArray(validationDictionary)) {
      Object.entries(validationDictionary).forEach(([field, messages]) => {
        const errorMessages = Array.isArray(messages) ? messages : [messages];
        errorMessages.forEach(message => {
          if (typeof message !== 'string') {
            return;
          }

          const controlPath = this.findControlPath(field);
          this.serverValidationErrors.push({
            field: field || 'Request',
            message,
            controlPath: controlPath ?? undefined
          });

          if (controlPath) {
            const control = this.proformaForm.get(controlPath);
            if (control) {
              const previousServerError = control.getError('serverError');
              control.setErrors({
                ...control.errors,
                serverError: previousServerError ? `${previousServerError} ${message}` : message
              });
              control.markAsTouched();
              const subscription = control.valueChanges.pipe(take(1)).subscribe(() => {
                const errors = {...(control.errors ?? {})};
                delete errors.serverError;
                control.setErrors(Object.keys(errors).length ? errors : null);
                this.serverValidationErrors = this.serverValidationErrors.filter(item => item.controlPath !== controlPath);
                this.saveErrorMessage = this.serverValidationErrors.length
                  ? 'Please correct the fields listed below and try again.'
                  : '';
                this.serverErrorSubscriptions = this.serverErrorSubscriptions.filter(item => item !== subscription);
              });
              this.serverErrorSubscriptions.push(subscription);
            }
          }
        });
      });
    }

    if (this.serverValidationErrors.length) {
      this.saveErrorMessage = 'Please correct the fields listed below and try again.';
      return;
    }

    this.saveErrorMessage = response.title
      ?? response.message
      ?? response.Message
      ?? error?.message
      ?? 'Unable to save the value sheet. Please try again.';
  }

  private scrollToTop(): void {
    if (typeof window !== 'undefined') {
      window.scrollTo({top: 0, behavior: 'smooth'});
    }
  }

  private findControlPath(field: string): string | null {
    const fieldSegments = this.normalisePath(field);
    if (!fieldSegments.length) {
      return null;
    }

    const controlPaths: string[] = [];
    const collectPaths = (control: AbstractControl, path: string[] = []): void => {
      if (control instanceof UntypedFormGroup) {
        Object.keys(control.controls).forEach(key => collectPaths(control.controls[key], [...path, key]));
      } else if (control instanceof UntypedFormArray) {
        control.controls.forEach((child, index) => collectPaths(child, [...path, String(index)]));
      } else if (path.length) {
        controlPaths.push(path.join('.'));
      }
    };
    collectPaths(this.proformaForm);

    const matches = controlPaths.filter(path => {
      const segments = this.normalisePath(path);
      return segments.length <= fieldSegments.length && segments.every((segment, index) =>
        fieldSegments[fieldSegments.length - segments.length + index] === segment
      );
    });
    if (matches.length === 1) {
      return matches[0];
    }

    const fieldName = fieldSegments[fieldSegments.length - 1];
    const leafMatches = controlPaths.filter(path => this.normalisePath(path).slice(-1)[0] === fieldName);
    return leafMatches.length === 1 ? leafMatches[0] : null;
  }

  private normalisePath(path: string): string[] {
    return path
      .replace(/\[(\d+)\]/g, '.$1')
      .split('.')
      .map(segment => segment.toLowerCase().replace(/[^a-z0-9]/g, ''))
      .filter(Boolean);
  }

  private clearServerValidationErrors(): void {
    this.serverErrorSubscriptions.forEach(subscription => subscription.unsubscribe());
    this.serverErrorSubscriptions = [];

    const clearErrors = (control: AbstractControl): void => {
      if (control instanceof UntypedFormGroup) {
        Object.values(control.controls).forEach(child => clearErrors(child));
      } else if (control instanceof UntypedFormArray) {
        control.controls.forEach(child => clearErrors(child));
      } else if (control.hasError('serverError')) {
        const errors = {...(control.errors ?? {})};
        delete errors.serverError;
        control.setErrors(Object.keys(errors).length ? errors : null);
      }
    };
    clearErrors(this.proformaForm);
  }

  private saveIwdCharge(iwd: VsExpectedIwd): Observable<VsExpectedIwd | null> {
    const hasDirection = iwd.leavingFrom?.warehouseId != null && iwd.deliveryTo?.warehouseId != null;
    const warehousesDiffer = iwd.leavingFrom?.warehouseId !== iwd.deliveryTo?.warehouseId;
    const hasCost = iwd.cost !== null
      && iwd.cost !== undefined
      && String(iwd.cost).trim() !== ''
      && Number.isFinite(Number(iwd.cost));

    if (!hasDirection || !warehousesDiffer || !hasCost) {
      return of(null);
    }

    const iwdCharge: VsExpectedIwd = {...iwd, cost: Number(iwd.cost)};
    if (iwd.expectedIwdId != null) {
      return this.pfiService.updateIwdCharge(iwdCharge);
    }

    return this.pfiService.addIwdCharge({...iwdCharge, expectedIwdId: undefined});
  }

  private areDatesReadyToSave(): boolean {
    const additionalDetails = this.proformaForm.get('vsAdditionalDetails') as UntypedFormGroup;
    if (additionalDetails.get('datesToBeSet')?.value) {
      return true;
    }

    let datesAreReady = true;
    requiredDateFields.forEach(field => {
      const control = additionalDetails.get(field);
      if (this.isDefaultDate(control?.value)) {
        control?.setErrors({defaultDate: true});
        control?.markAsTouched();
        datesAreReady = false;
      }
    });

    if (!datesAreReady) {
      return false;
    }

    const paymentReceivedBy = this.getDateTimestamp(additionalDetails.get('paymentReceivedBy')?.value);
    const dispatchBy = this.getDateTimestamp(additionalDetails.get('dispatchBy')?.value);
    const deliveryDate = this.getDateTimestamp(additionalDetails.get('deliveryDate')?.value);

    if (dispatchBy < paymentReceivedBy) {
      additionalDetails.get('dispatchBy')?.setErrors({dateSequence: true});
      additionalDetails.get('dispatchBy')?.markAsTouched();
      datesAreReady = false;
    }

    if (deliveryDate < dispatchBy) {
      additionalDetails.get('deliveryDate')?.setErrors({dateSequence: true});
      additionalDetails.get('deliveryDate')?.markAsTouched();
      datesAreReady = false;
    }

    return datesAreReady;
  }

  private isDefaultDate(value: unknown): boolean {
    if (!value) {
      return true;
    }

    const date = value instanceof Date ? value : new Date(value as string);
    return Number.isNaN(date.getTime()) || date.getFullYear() <= 1;
  }

  private getDateTimestamp(value: unknown): number {
    const date = value instanceof Date ? value : new Date(value as string);
    return Date.UTC(date.getFullYear(), date.getMonth(), date.getDate());
  }

  getMinimumDate(controlName: string): Date | null {
    const previousControlName = controlName === 'dispatchBy'
      ? 'paymentReceivedBy'
      : controlName === 'deliveryDate'
        ? 'dispatchBy'
        : null;

    if (!previousControlName) {
      return null;
    }

    const value = this.proformaForm.get(`vsAdditionalDetails.${previousControlName}`)?.value;
    return this.isDefaultDate(value) ? null : value instanceof Date ? value : new Date(value);
  }

  private initSplit(vsSplit: any[]): UntypedFormArray {
    return this.fb.array(vsSplit.map(this.createVsSplitRow.bind(this)));
  }

  private createVsSplitRow(splitItem: any): UntypedFormGroup {
    return this.fb.group({
      warehouse: this.fb.group({
        warehouseId: [splitItem?.warehouse?.warehouseId ?? null],
        city: [splitItem?.warehouse?.city ?? ''],
        region: [splitItem?.warehouse?.region ?? '']
      }),
      commodity: this.fb.group({
        commodityID: [splitItem?.commodity?.commodityID ?? null],
        letter: [splitItem?.commodity?.letter ?? ''],
        name: [splitItem?.commodity?.name ?? '']
      }),
      warehouseLineCount: [splitItem?.warehouseLineCount ?? 0],
      warehouseGoodsValue: [splitItem?.warehouseGoodsValue ?? 0],
      warehouseProfitValue: [splitItem?.warehouseProfitValue ?? 0],
      warehouseHandlingValue: [splitItem?.warehouseHandlingValue ?? 0],
      warehouseInlandValue: [splitItem?.warehouseInlandValue ?? 0],
      warehouseExportValue: this.fb.control({
        value: this.formatCurrencyValue(splitItem?.warehouseExportValue),
        disabled: true
      }),
      warehouseTotalValue: this.fb.control({
        value: this.formatCurrencyValue(splitItem?.warehouseTotalValue),
        disabled: true
      })
    });
  }

  private initIwd(vsIwd: any): UntypedFormGroup {
    const group = this.fb.group({
      expectedIwdId: [vsIwd?.expectedIwdId ?? null],
      leavingFrom: [vsIwd?.leavingFrom ?? null],
      deliveryTo: [vsIwd?.deliveryTo ?? null],
      cost: [vsIwd?.cost == null ? '' : this.formatCurrencyValue(vsIwd.cost)]
    });

    group.get('cost')?.valueChanges.subscribe(() => {
      this.updateWarehouseTotals(Number(this.proformaForm.get('vsBreakdown.total')?.value ?? 0));
    });

    return group;
  }

  // Build the pivot table for predictions.
  private initPredictions(vsPredictions: any[]): UntypedFormArray {
    // 1. Collect unique warehouses.
    const warehouseMap = new Map<number, string>();
    vsPredictions.forEach(item => {
      warehouseMap.set(item.warehouseID, item.warehouse);
    });
    this.uniqueWarehouses = Array.from(warehouseMap.entries()).map(([id, name]) => ({
      id,
      name,
      colDef: `warehouse_${id}`
    }));

    // 2. Group predictions by method.
    const methodMap = new Map<number, GroupedPrediction>();
    vsPredictions.forEach(item => {
      if (!methodMap.has(item.methodID)) {
        methodMap.set(item.methodID, {
          methodID: item.methodID,
          method: item.method,
          incoterm: item.incoterm || '',
          warehouses: {}
        });
      }

      const group = methodMap.get(item.methodID);
      if (!group) {
        return;
      }

      group.warehouses[item.warehouseID] = {
        pricePerKilo: item.pricePerKilo,
        totGoods: item.totGoods,
        totFreight: item.totFreight,
        predPercentage: item.predPercentage,
        predAccuracy: item.predAccuracy
      };
    });

    // 3. Build columns for the pivot table.
    this.displayedColumnsPredictions = ['method', 'incoterm', ...this.uniqueWarehouses.map(w => w.colDef)];

    // 4. Build the FormArray.
    const rows: UntypedFormGroup[] = [];
    methodMap.forEach((group: GroupedPrediction) => {
      const groupObj: { [key: string]: UntypedFormControl | UntypedFormGroup } = {
        methodID: new UntypedFormControl(group.methodID),
        method: new UntypedFormControl(group.method),
        incoterm: new UntypedFormControl(group.incoterm)
      };
      this.uniqueWarehouses.forEach(w => {
        const warehouseData: PredictionData = group.warehouses[w.id] || {
          pricePerKilo: 0,
          totGoods: 0,
          totFreight: 0,
          predPercentage: 0,
          predAccuracy: 0
        };
        groupObj[w.colDef] = this.fb.group({
          pricePerKilo: [warehouseData.pricePerKilo],
          totGoods: [warehouseData.totGoods],
          totFreight: [warehouseData.totFreight],
          predPercentage: [warehouseData.predPercentage],
          predAccuracy: [warehouseData.predAccuracy]
        });
      });
      rows.push(this.fb.group(groupObj));
    });

    return this.fb.array(rows);
  }

  private initBreakdown(breakdown: any): UntypedFormGroup {
    const fg = this.fb.group({
      freightToPort: [this.formatCurrencyValue(breakdown?.freightToPort?.chargeValue)],
      destinationCharge: [this.formatCurrencyValue(breakdown?.destinationCharge?.chargeValue)],
      dutiesAndTaxes: [this.formatCurrencyValue(
        this.getFreightValueForPercentage(
          Number(breakdown?.dutiesAndTaxes?.chargeValue ?? 0) * 100,
          breakdown?.freightToPort?.chargeValue
        )
      )],
      legalisation: [this.formatCurrencyValue(breakdown?.legalisation?.chargeValue)],
      certOfOrigin: [this.formatCurrencyValue(breakdown?.certOfOrigin?.chargeValue)],
      freightToPortPercentage: [this.getGoodsPercentage(breakdown?.freightToPort?.chargeValue)],
      dutiesAndTaxesPercentage: [Number(breakdown?.dutiesAndTaxes?.chargeValue ?? 0) * 100],
      freightToPortMarkup: [breakdown?.freightToPort?.markUp ?? false],
      destinationChargeMarkup: [breakdown?.destinationCharge?.markUp ?? false],
      dutiesAndTaxesMarkup: [breakdown?.dutiesAndTaxes?.markUp ?? false],
      legalisationMarkup: [breakdown?.legalisation?.markUp ?? false],
      certOfOriginMarkup: [breakdown?.certOfOrigin?.markUp ?? false],

      // Additional charges array
      additionalCharges: this.fb.array(
        (breakdown?.additionalCharges?.length ? breakdown.additionalCharges : [{}]).map((c: any) =>
          this.fb.group({
            description: [c.description ?? ''],
            chargeValue: [this.formatCurrencyValue(c.chargeValue)],
            markUp: [c.markUp ?? false]
          })
        ) ?? []
      ),

      total: [this.formatCurrencyValue(breakdown?.total)]
    });

    ['freightToPort', 'destinationCharge', 'dutiesAndTaxes', 'legalisation', 'certOfOrigin'].forEach(key => {
      fg.get(key)?.valueChanges.subscribe(() => this.updateBreakdownTotal(fg));
    });

    const freightValueControl = fg.get('freightToPort');
    const freightPercentageControl = fg.get('freightToPortPercentage');
    const dutiesValueControl = fg.get('dutiesAndTaxes');
    const dutiesPercentageControl = fg.get('dutiesAndTaxesPercentage');

    freightPercentageControl?.valueChanges.subscribe(percentage => {
      freightValueControl?.setValue(this.getGoodsValueForPercentage(percentage), { emitEvent: false });
      this.updateBreakdownTotal(fg);
    });
    freightValueControl?.valueChanges.subscribe(value => {
      freightPercentageControl?.setValue(this.getGoodsPercentage(value), { emitEvent: false });
      dutiesValueControl?.setValue(
        this.getFreightValueForPercentage(dutiesPercentageControl?.value, value),
        { emitEvent: false }
      );
      this.updateBreakdownTotal(fg);
    });

    dutiesPercentageControl?.valueChanges.subscribe(percentage => {
      dutiesValueControl?.setValue(
        this.getFreightValueForPercentage(percentage, freightValueControl?.value),
        { emitEvent: false }
      );
      this.updateBreakdownTotal(fg);
    });
    dutiesValueControl?.valueChanges.subscribe(value => {
      dutiesPercentageControl?.setValue(
        this.getFreightPercentage(value, freightValueControl?.value),
        { emitEvent: false }
      );
    });

    this.vsBreakdownFields.forEach(field => {
      fg.get(`${field.controlName}Markup`)?.valueChanges.subscribe(() => this.updateBreakdownTotal(fg));
    });

    // Subscribe to additional charges changes
    (fg.get('additionalCharges') as UntypedFormArray).controls.forEach(cg => {
      cg.get('chargeValue')?.valueChanges.subscribe(() => this.updateBreakdownTotal(fg));
      cg.get('markUp')?.valueChanges.subscribe(() => this.updateBreakdownTotal(fg));
    });

    this.updateBreakdownTotal(fg);
    return fg;
  }

  get additionalCharges(): UntypedFormArray {
    return this.proformaForm.get('vsBreakdown')?.get('additionalCharges') as UntypedFormArray;
  }

  addCharge(): void {
    const newCharge = this.fb.group({
      description: [''],
      chargeValue: [this.formatCurrencyValue(0)],
      markUp: [false]
    });
    this.additionalCharges.push(newCharge);

    newCharge.get('chargeValue')?.valueChanges.subscribe(() => {
      this.updateBreakdownTotal(this.proformaForm.get('vsBreakdown') as UntypedFormGroup);
    });
    newCharge.get('markUp')?.valueChanges.subscribe(() => {
      this.updateBreakdownTotal(this.proformaForm.get('vsBreakdown') as UntypedFormGroup);
    });

    this.updateBreakdownTotal(this.proformaForm.get('vsBreakdown') as UntypedFormGroup);
  }

  removeCharge(index: number): void {
    this.additionalCharges.removeAt(index);
    this.updateBreakdownTotal(this.proformaForm.get('vsBreakdown') as UntypedFormGroup);
  }

  private updateBreakdownTotal(fg: UntypedFormGroup) {
    const mainSum = ['freightToPort', 'destinationCharge',
      'dutiesAndTaxes', 'legalisation', 'certOfOrigin']
      .reduce((sum, key) => {
        const value = Number(fg.get(key)?.value ?? 0);
        const markedUp = fg.get(`${key}Markup`)?.value
          ? this.getMarkedUpValue(value)
          : value;

        return sum + markedUp;
      }, 0);

    const addSum = (fg.get('additionalCharges') as UntypedFormArray).controls
      .reduce((sum, charge) => {
        const value = Number(charge.get('chargeValue')?.value ?? 0);
        const markedUp = charge.get('markUp')?.value
          ? this.getMarkedUpValue(value)
          : value;

        return sum + markedUp;
      }, 0);

    const freightTotal = this.roundCurrency(mainSum + addSum);
    fg.get('total')?.setValue(this.formatCurrencyValue(freightTotal), { emitEvent: false });
    this.updateWarehouseTotals(freightTotal);
  }

  private roundCurrency(value: number): number {
    return Number(Number(value ?? 0).toFixed(2));
  }

  getMarkedUpValue(value: unknown): number {
    const amount = Number(value ?? 0);
    return this.roundCurrency(amount / 0.9);
  }

  private formatCurrencyValue(value: number): string {
    return this.roundCurrency(Number(value ?? 0)).toFixed(2);
  }

  get costWarnings(): string[] {
    const totalGoods = this.getTotalGoodsValue();
    if (!this.proformaForm || totalGoods <= 0) {
      return [];
    }

    const checks = [
      {label: 'Freight', controlName: 'freightToPort'},
      {label: 'Duties & Taxes', controlName: 'dutiesAndTaxes'}
    ];
    return checks.reduce<string[]>((warnings, {label, controlName}) => {
      const amount = Number(this.proformaForm.get(`vsBreakdown.${controlName}`)?.value ?? 0);
      const percentage = amount / totalGoods * 100;
      if (Number.isFinite(percentage) && percentage > this.costWarningThresholdPercent) {
        warnings.push(`${label} is ${percentage.toFixed(1)}% of the goods value, which is over ${this.costWarningThresholdPercent}%. Please check this is correct.`);
      }
      return warnings;
    }, []);
  }

  private getTotalGoodsValue(): number {
    return this.vsSplitRows.reduce(
      (sum, row) => sum + Number(row.get('warehouseGoodsValue')?.value ?? 0),
      0
    );
  }

  private getGoodsPercentage(value: number): number {
    const totalGoods = this.getTotalGoodsValue();
    return totalGoods > 0 ? Number(((Number(value ?? 0) / totalGoods) * 100).toFixed(2)) : 0;
  }

  private getGoodsValueForPercentage(percentage: number): number {
    return Number((this.getTotalGoodsValue() * (Number(percentage ?? 0) / 100)).toFixed(2));
  }

  private getFreightPercentage(value: number, freightValue: number): number {
    const freight = Number(freightValue ?? 0);
    return freight > 0 ? Number(((Number(value ?? 0) / freight) * 100).toFixed(2)) : 0;
  }

  private getFreightValueForPercentage(percentage: number, freightValue: number): number {
    return Number((Number(freightValue ?? 0) * (Number(percentage ?? 0) / 100)).toFixed(2));
  }

  private getIwdSourceId(): number | null {
    const sourceId = this.proformaForm.get('vsIwd.leavingFrom')?.value?.warehouseId;
    return sourceId != null && Number.isFinite(Number(sourceId)) ? Number(sourceId) : null;
  }

  private onIwdDestinationChange(): void {
    const deliveryToId = this.proformaForm.get('vsAdditionalDetails.leavingFrom')?.value?.warehouseId;
    const leavingFromControl = this.proformaForm.get('vsIwd.leavingFrom');
    if (deliveryToId != null && leavingFromControl?.value?.warehouseId === deliveryToId) {
      leavingFromControl.setValue(null);

      const costControl = this.proformaForm.get('vsIwd.cost');
      const rawCost = costControl?.value;
      if (rawCost !== null && rawCost !== undefined && String(rawCost).trim() !== '') {
        costControl.setValue('');
        this.iwdClearedNotice = 'The IWD From warehouse matched the main Leaving From warehouse, so the IWD was removed and its cost cleared.';
      }
    }

    this.updateWarehouseTotals(Number(this.proformaForm.get('vsBreakdown.total')?.value ?? 0));
  }

  getIwdCostForRow(row: UntypedFormGroup): number {
    const sourceId = this.getIwdSourceId();
    const rowWarehouseId = row.get('warehouse.warehouseId')?.value;
    const rawCost = this.proformaForm.get('vsIwd.cost')?.value;

    if (sourceId == null || rawCost == null || rawCost === '') {
      return 0;
    }

    const cost = Number(rawCost);
    if (!Number.isFinite(cost)) {
      return 0;
    }

    const sourceRows = this.vsSplitRows.filter(splitRow =>
      Number(splitRow.get('warehouse.warehouseId')?.value) === sourceId
    );
    const rowIndex = sourceRows.indexOf(row);
    if (rowIndex < 0 || Number(rowWarehouseId) !== sourceId) {
      return 0;
    }

    const goodsValues = sourceRows.map(splitRow => Math.max(
      0,
      Number(splitRow.get('warehouseGoodsValue')?.value ?? 0)
    ));
    const totalGoods = goodsValues.reduce((sum, goodsValue) => sum + goodsValue, 0);
    if (totalGoods <= 0) {
      return rowIndex === 0 ? cost : 0;
    }

    if (rowIndex === sourceRows.length - 1) {
      const previouslyAllocated = goodsValues.slice(0, -1).reduce(
        (sum, goodsValue) => sum + this.roundCurrency(cost * goodsValue / totalGoods),
        0
      );
      return this.roundCurrency(cost - previouslyAllocated);
    }

    return this.roundCurrency(cost * goodsValues[rowIndex] / totalGoods);
  }

  private updateWarehouseTotals(freightTotal: number): void {
    const splitRows = this.vsSplitRows;
    const totalGoods = splitRows.reduce(
      (sum, row) => sum + Number(row.get('warehouseGoodsValue')?.value ?? 0),
      0
    );

    splitRows.forEach((row, index) => {
      const goodsValue = Number(row.get('warehouseGoodsValue')?.value ?? 0);
      const exportValue = totalGoods > 0
        ? freightTotal * (goodsValue / totalGoods)
        : index === 0 ? freightTotal : 0;
      const totalValue = goodsValue
        + Number(row.get('warehouseHandlingValue')?.value ?? 0)
        + Number(row.get('warehouseInlandValue')?.value ?? 0)
        + this.getIwdCostForRow(row)
        + exportValue;

      row.get('warehouseExportValue')?.setValue(this.formatCurrencyValue(exportValue), { emitEvent: false });
      row.get('warehouseTotalValue')?.setValue(this.formatCurrencyValue(totalValue), { emitEvent: false });
    });
  }

  private initAdditionalDetails(vsAdditionalDetails: any): UntypedFormGroup {
    const leavingFrom =
      this.warehouseList?.find(
        w => w.warehouseId === vsAdditionalDetails?.leavingFrom?.warehouseId
      ) ?? vsAdditionalDetails?.leavingFrom ?? null;

    const freightMethod =
      this.methodList?.find(
        w => w.methodId === vsAdditionalDetails?.freightMethod?.methodId
      ) ?? null;

    const incoterm =
      this.incotermList?.find(
        w => w.incotermId === vsAdditionalDetails?.incoterm?.incotermId
      ) ?? null;

    return this.fb.group({
      leavingFrom: [leavingFrom],
      freightMethod: [freightMethod],
      incoterm: [incoterm],
      incotermLocation: [vsAdditionalDetails?.incotermLocation ?? ''],
      destinationPort: [vsAdditionalDetails?.destinationPort ?? ''],
      paymentReceivedBy: [vsAdditionalDetails?.paymentReceivedBy ?? ''],
      dispatchBy: [vsAdditionalDetails?.dispatchBy ?? ''],
      deliveryDate: [vsAdditionalDetails?.deliveryDate ?? ''],
      datePaymentRec: [vsAdditionalDetails?.datePaymentRec ?? ''],
      amountReceived: [vsAdditionalDetails?.amountReceived ?? 0],
      proformaReleased: [vsAdditionalDetails?.proformaReleased ?? false],
      paymentAllocated: [vsAdditionalDetails?.paymentAllocated ?? false],
      datesToBeSet: [vsAdditionalDetails?.datesToBeSet ?? false],
      freightToBeConfirmed: [
        this.pfi?.exportfreighttbc ??
        this.pfi?.exportFreightTbc ??
        this.pfi?.ExportFreightTbc ??
        vsAdditionalDetails?.freightToBeConfirmed ??
        false
      ],
    });
  }

  getPredictionPercentage(row: UntypedFormGroup, warehouseId: number): number {
    return Number(row.get(`warehouse_${warehouseId}`)?.get('predPercentage')?.value ?? 0);
  }

  // Computes the predicted value based on predPercentage and corresponding warehouseGoodsValue.
  computePrediction(row: UntypedFormGroup, warehouseId: number): number {
    const warehouseGroup = row.get(`warehouse_${warehouseId}`) as UntypedFormGroup;
    if (!warehouseGroup) {
      return 0;
    }
    const predPercentage = warehouseGroup.get('predPercentage')?.value || 0;
    let goodsValue = 0;
    for (const control of this.vsSplit.controls) {
      const warehouseControl = control.get('warehouse');
      if (warehouseControl && warehouseControl.get('warehouseId')?.value === warehouseId) {
        goodsValue = control.get('warehouseGoodsValue')?.value || 0;
        break;
      }
    }
    return (predPercentage / 100) * goodsValue;
  }

  private convertPivotPredictions(
    pivotRows: any[],
    uniqueWarehouses: { id: number; name: string; colDef: string }[]
  ): any[] {
    const predictions: any[] = [];
    const warehouseLookup = new Map<number, string>();
    uniqueWarehouses.forEach(w => {
      warehouseLookup.set(w.id, w.name);
    });
    pivotRows.forEach(row => {
      const methodID = row.methodID;
      const method = row.method;
      const incoterm = row.incoterm;
      Object.keys(row).forEach(key => {
        if (key.startsWith('warehouse_')) {
          const warehouseID = parseInt(key.split('_')[1], 10);
          const fieldGroup = row[key];
          if (fieldGroup && typeof fieldGroup === 'object') {
            predictions.push({
              methodID: methodID,
              method: method,
              incoterm: incoterm,
              warehouseID: warehouseID,
              warehouse: warehouseLookup.get(warehouseID) || '',
              pricePerKilo: fieldGroup.pricePerKilo,
              totGoods: fieldGroup.totGoods,
              totFreight: fieldGroup.totFreight,
              predPercentage: fieldGroup.predPercentage,
              predAccuracy: fieldGroup.predAccuracy
            });
          }
        }
      });
    });
    return predictions;
  }

  onToggleFreightToBeConfirmed(): void {
    const details = this.proformaForm.get('vsAdditionalDetails') as UntypedFormGroup;
    const disable = details.get('freightToBeConfirmed')?.value;

    const valueGroups = ['vsBreakdown', 'vsIwd'];
    valueGroups.forEach(path => {
      const group = this.proformaForm.get(path);
      disable ? group?.disable() : group?.enable();
    });
  }

  onToggleDatesToBeSet(): void {
    const details = this.proformaForm.get('vsAdditionalDetails') as UntypedFormGroup;
    const disable = details.get('datesToBeSet')?.value;

    ['paymentReceivedBy', 'dispatchBy', 'deliveryDate', 'datePaymentRec'].forEach(field => {
      const control = details.get(field);
      disable ? control?.disable() : control?.enable();
    });
  }

  private convertToPfiEntity(formData: any): Proforma & {vsIwd: VsExpectedIwd} {
    const datesToBeSet = Boolean(formData.vsAdditionalDetails.datesToBeSet);

    return {
      ...this.pfi,
      invoicedBy: 'EquipMySchool Ltd',
      vsSplit: formData.vsSplit,
      notAvailable: formData.notAvailable,
      vsPredictions: this.convertPivotPredictions(formData.vsPredictions, this.uniqueWarehouses),
      vsIwd: {
        ...formData.vsIwd,
        deliveryTo: formData.vsAdditionalDetails.leavingFrom,
        pfiId: this.pfi.pfiId
      },
      vsBreakdown: {
        // main charges as objects
        ...this.vsBreakdownFields.reduce((acc, f) => {
          acc[f.controlName] = {
            description: f.controlName,
            chargeValue: f.controlName === 'dutiesAndTaxes'
              ? Number(formData.vsBreakdown.dutiesAndTaxesPercentage ?? 0) / 100
              : Number(formData.vsBreakdown[f.controlName] ?? 0),
            markUp: Boolean(formData.vsBreakdown[`${f.controlName}Markup`])
          };
          return acc;
        }, {} as Record<string, VsAdditionalCharge>),

        // user-added additional charges
        additionalCharges: (formData.vsBreakdown.additionalCharges || []).map((charge: any) => ({
          description: charge.description,
          chargeValue: Number(charge.chargeValue ?? 0),
          markUp: Boolean(charge.markUp)
        })),

        total: formData.vsBreakdown.total
      },
      vsAdditionalDetails: {
        leavingFrom: formData.vsAdditionalDetails.leavingFrom,
        freightMethod: formData.vsAdditionalDetails.freightMethod,
        incoterm: formData.vsAdditionalDetails.incoterm,
        incotermLocation: formData.vsAdditionalDetails.incotermLocation?.trim() || null,
        destinationPort: formData.vsAdditionalDetails.destinationPort,
        paymentReceivedBy: datesToBeSet ? null : formData.vsAdditionalDetails.paymentReceivedBy,
        dispatchBy: datesToBeSet ? null : formData.vsAdditionalDetails.dispatchBy,
        deliveryDate: datesToBeSet ? null : formData.vsAdditionalDetails.deliveryDate,
        datePaymentRec: datesToBeSet ? null : formData.vsAdditionalDetails.datePaymentRec,
        amountReceived: formData.vsAdditionalDetails.amountReceived,
        proformaReleased: formData.vsAdditionalDetails.proformaReleased,
        paymentAllocated: formData.vsAdditionalDetails.paymentAllocated,
        datesToBeSet,
        freightToBeConfirmed: formData.vsAdditionalDetails.freightToBeConfirmed,
      }
    };
  }

  compareWarehouses = (a: Warehouse, b: Warehouse) => a?.warehouseId === b?.warehouseId;
  compareMethods = (a: FreightMethod, b: FreightMethod) => a?.methodId === b?.methodId;
  compareIncoterms = (a: Incoterm, b: Incoterm) => a?.incotermId === b?.incotermId;

  onFreightMethodChange(method: FreightMethod | null): void {
    const incotermControl = this.proformaForm.get('vsAdditionalDetails.incoterm');
    this.updateIncotermLocationValidation(method);
    const selectedIncotermId = incotermControl?.value?.incotermId;
    const isRelevant = (this.incotermList ?? []).some(incoterm =>
      incoterm.incotermId === selectedIncotermId
      && incoterm.relevantTo?.some(relevantMethod => relevantMethod.methodId === method?.methodId)
    );

    if (selectedIncotermId != null && !isRelevant) {
      incotermControl?.setValue(null);
    }
  }

  private updateIncotermLocationValidation(method: FreightMethod | null): void {
    const locationControl = this.proformaForm.get('vsAdditionalDetails.incotermLocation');
    locationControl?.setValidators(
      method?.methodId === 70012
        ? null
        : control => control.value?.trim() ? null : {required: true}
    );
    locationControl?.updateValueAndValidity({emitEvent: false});
  }

  openInlandChargesDialog(): void {
    this.dialog.open(InlandChargesDialogComponent, {
      width: '850px',
      data: {pfiId: this.pfi.pfiId}
    });
  }
}

